/**
 * How the new site writes a live figure. Kept out of the components so each budget can be tested
 * without importing React, the way `home/stat-figure.ts` is.
 *
 * Shared rather than per-section: the about-us band and the solutions deck both abbreviate a
 * seven-digit total, and two implementations of "2,8M" would drift into disagreeing about the site's
 * own numbers.
 */
import { formatNumber } from '@worldcoo/ui/lib/utils';

/**
 * A count shown IN FULL, with no leading `+`.
 *
 * `alwaysGroup` because Spanish sets `minimumGroupingDigits: 2`, so `Intl` leaves a four-digit count
 * as "5000" and only groups from five digits up — and Italian normalizes to Spanish, so it reads the
 * same way. The points-of-sale figure is four digits, which is exactly that case.
 */
export function formatBandFigure(locale: string, value: number): string {
	return formatNumber(locale, value, 0, { alwaysGroup: true });
}

/**
 * A figure abbreviated to millions the way the hand-off draws it — "2M", not "2.763.110".
 *
 * ONE decimal, because the figure is live: the review wrote "2M" believing that was the count, and
 * flooring a real 2.763.110 to "2M" would under-report it by three quarters of a million. The decimal
 * separator is the locale's, so Spanish reads "2,8M" and English "2.8M".
 *
 * A whole number of millions drops the decimal rather than printing "2,0M" — which is what makes the
 * BI-failure fallback of exactly 2.000.000 render as the "2M" the design drew.
 *
 * `formatCompactNumber` in `@worldcoo/ui` is deliberately not used: it prepends a `+`, which the
 * review removed from every figure on the site, and it hardcodes the unit in English.
 */
export function formatMillions(locale: string, value: number): string {
	// Below a million the abbreviation would round everything to "0M", so print the real count.
	if (value < 1_000_000) return formatBandFigure(locale, value);

	const millions = Math.round(value / 100_000) / 10;
	return Number.isInteger(millions) ? `${formatNumber(locale, millions, 0)}M` : `${formatNumber(locale, millions, 1)}M`;
}

/**
 * An amount of money abbreviated for a box too narrow to hold it in full — "8,2M €", "517K €".
 *
 * The thousands step is what `formatMillions` alone cannot do: it prints anything under a million in
 * full, and a partner that raised 517.482 € would render eleven characters into a card that has room
 * for about six. Below a thousand there is nothing to abbreviate, so the figure is printed as it is.
 *
 * Rounded, not floored, and to the same one decimal as `formatMillions` — the site must not read
 * "8,2M €" in one section and "8,1M €" in another for the same total. The cents are dropped on the
 * way in for the same reason: an abbreviation has no use for them.
 */
export function formatCompactMoney(locale: string, euros: number): string {
	if (euros >= 1_000_000) return `${formatMillions(locale, euros)} €`;
	if (euros >= 1_000) return `${formatBandFigure(locale, Math.round(euros / 1_000))}K €`;
	return `${formatBandFigure(locale, Math.round(euros))} €`;
}
